import React, { useEffect, useMemo, useState, useRef, useCallback } from "react";
import { createPortal } from "react-dom";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { CalendarIcon, Save, X, ClipboardCheck, Camera, AlertTriangle } from "lucide-react";
import { cn } from "@/lib/utils";
import { formatDate } from "@/lib/dates";
import { supabase } from "@/lib/supabase";
import { toast } from "@/components/ui/use-toast";
import { loadVehicleTypes, type VehicleTypeRow } from "@/lib/vehicleTypes";
import { loadCheckItemTypeRestrictions, checkItemAppliesToType, type CheckItemTypeRestrictions } from "@/lib/vehicleCheckItems";
import { recordInspectionOdometer } from "@/lib/vehicleOdometer";
import {
  applyDraftToChecklist,
  buildDraftItems,
  computeInspectionProgress,
  isDraftFresh,
  type InspectionDraft,
} from "@/lib/adminInspectionProgress";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import DamageMarkerOverlay, { DamageMarker } from "./DamageMarkerOverlay";
import KnownDamagePanel from "./KnownDamagePanel";
import { loadMarkersForPhotos, markerContentKey, syncMarkersForPhoto, type FormMarker } from "@/lib/damageData";



type Status = "OK" | "Needs Attention" | "Needs Fixing Soon" | "Unsafe";

type ChecklistItem = {
  category: string;
  item_key: string;
  label: string;
  status: Status;
  note: string;
  /** True once the admin has actually answered this item (untouched items don't count towards the %). */
  confirmed?: boolean;
};

type Vehicle = {
  vehicle_number: string;
  make?: string | null;
  model?: string | null;
  year?: number | null;
  registration?: string | null;
  vehicle_type?: string | null;
};

type VehicleSetupRecord = {
  psl_holder_required: string;
  loading_certificate_required: string;
  kiddilock_signs_required: string;
  fleet_number_required: string;
  door_sign_required: string;
  fleet_numbers_lr_required: string;
  fleet_number_rear_required: string;
  taxi_stand_coupon_required: string;
  roof_rack_required: string;
  top_light_required: string;
  camera_required: string;
  hardwire_usb_required: string;
  eftpos_machine_required: string;
  tablet_holder_required: string;
  eftpos_charger_required: string;
  driver_seat_inspection1: string;
  front_passenger_inspection1: string;
  rear_seats_inspection1: string;
  interior_inspection1: string;
};

interface AdminVehicleInspectionModalProps {
  isOpen: boolean;
  onClose: () => void;
  vehicle: Vehicle | null;
  onSaved: () => void;
}

type CaptureKey = "exterior_front" | "exterior_rear" | "exterior_driver" | "exterior_passenger";
type OverlayView = "front" | "rear" | "driver" | "passenger";

const CAPTURE_KEYS: CaptureKey[] = ["exterior_front", "exterior_rear", "exterior_driver", "exterior_passenger"];
const VIEW_LABELS: Record<CaptureKey, string> = {
  exterior_front: "FRONT",
  exterior_rear: "REAR",
  exterior_driver: "DRIVER Side",
  exterior_passenger: "PASSENGER Side",
};

const STATUS_OPTIONS: { value: Status; label: string }[] = [
  { value: "OK", label: "OK" },
  { value: "Needs Attention", label: "Needs Attention" },
  { value: "Needs Fixing Soon", label: "Needs Fixing Soon" },
  { value: "Unsafe", label: "Unsafe / Not OK" },
];

const STATUS_COLOR: Record<Status, string> = {
  OK: "#22c55e",
  "Needs Attention": "#eab308",
  "Needs Fixing Soon": "#f97316",
  Unsafe: "#ef4444",
};

const BUCKET_VEHICLE_INSPECTIONS = "vehicle-inspections";

// Signage items mapping
const SIGNAGE_ITEMS = [
  { key: "psl_holder_required", label: "PSL Holder" },
  { key: "loading_certificate_required", label: "Loading Certificate" },
  { key: "kiddilock_signs_required", label: "Kiddilock Signs" },
  { key: "fleet_number_required", label: "Fleet Number" },
  { key: "door_sign_required", label: "Door Sign" },
  { key: "fleet_numbers_lr_required", label: "Fleet Numbers L/R" },
  { key: "fleet_number_rear_required", label: "Fleet Number Rear" },
  { key: "taxi_stand_coupon_required", label: "Taxi Stand Coupon" },
];

// Hardware items mapping
const HARDWARE_ITEMS = [
  { key: "roof_rack_required", label: "Roof Rack" },
  { key: "top_light_required", label: "Top Light" },
  { key: "camera_required", label: "Camera" },
  { key: "hardwire_usb_required", label: "Hardwire USB" },
  { key: "eftpos_machine_required", label: "EFTPOS Machine" },
  { key: "tablet_holder_required", label: "Tablet Holder" },
  { key: "eftpos_charger_required", label: "EFTPOS Charger" },
];

// Inspection items mapping
const INSPECTION_ITEMS = [
  { key: "driver_seat_inspection1", label: "Driver Seat" },
  { key: "front_passenger_inspection1", label: "Front Passenger Seat" },
  { key: "rear_seats_inspection1", label: "Rear Seats" },
  { key: "interior_inspection1", label: "Interior Condition" },
];

function computeOverallStatus(items: ChecklistItem[]): Status {
  const order: Status[] = ["OK", "Needs Attention", "Needs Fixing Soon", "Unsafe"];
  let idx = 0;
  for (const it of items) {
    const i = order.indexOf(it.status);
    if (i > idx) idx = i;
  }
  return order[idx];
}

function safePathPart(s: string) {
  return s.replace(/[^a-zA-Z0-9-_]+/g, "_");
}

function isIOSDevice() {
  if (typeof window === "undefined") return false;
  const ua = window.navigator.userAgent || "";
  return /iPad|iPhone|iPod/.test(ua);
}

function isStandalonePWA() {
  if (typeof window === "undefined") return false;
  const navAny = window.navigator as any;
  const iosStandalone = !!navAny.standalone;
  const displayModeStandalone = window.matchMedia?.("(display-mode: standalone)")?.matches ?? false;
  return iosStandalone || displayModeStandalone;
}

function aspectToPaddingTop(aspect: string) {
  const parts = aspect.split("/").map((s) => parseFloat(s.trim()));
  const w = parts[0] || 9;
  const h = parts[1] || 16;
  return `${(h / w) * 100}%`;
}

function getOverlayStoragePath(overlayAssetKey: string, view: OverlayView) {
  return `${overlayAssetKey}/${overlayAssetKey}_${view}.png`;
}

function getFrameSpec(k: CaptureKey) {
  if (k === "exterior_front" || k === "exterior_rear") {
    return { aspect: "9 / 16", label: "Portrait (9:16)" };
  }
  if (k === "exterior_driver" || k === "exterior_passenger") {
    return { aspect: "16 / 9", label: "Landscape (16:9)" };
  }
  return { aspect: "3 / 4", label: "Portrait (3:4)" };
}

function captureKeyToView(k: CaptureKey): OverlayView {
  if (k === "exterior_front") return "front";
  if (k === "exterior_rear") return "rear";
  if (k === "exterior_driver") return "driver";
  return "passenger";
}

function makeCalKey(vehicleType: string, view: OverlayView) {
  return `overlay_cal_${vehicleType}_${view}`;
}

// Helper to get logged in user name from localStorage
function getLoggedInUserName(): string {
  // Check both possible localStorage keys
  const loggedUser = localStorage.getItem('loggedUser');
  const currentUser = localStorage.getItem('currentUser');
  
  if (loggedUser) {
    try {
      const userData = JSON.parse(loggedUser);
      if (userData.name) return userData.name;
    } catch (e) {
      console.error("Error parsing loggedUser:", e);
    }
  }
  
  if (currentUser) {
    try {
      const userData = JSON.parse(currentUser);
      if (userData.name) return userData.name;
      if (userData.driver_name) return userData.driver_name;
    } catch (e) {
      console.error("Error parsing currentUser:", e);
    }
  }
  
  return "";
}

const Section: React.FC<{ title: string; subtitle?: string; children: React.ReactNode }> = ({ title, subtitle, children }) => (
  <div className="rounded-lg border border-gray-700/60 bg-gray-900/30 p-4">
    <div className="mb-3">
      <div className="text-sm font-semibold text-amber-500">{title}</div>
      {subtitle ? <div className="text-xs text-gray-400 mt-1">{subtitle}</div> : null}
    </div>
    {children}
  </div>
);

const AdminVehicleInspectionModal: React.FC<AdminVehicleInspectionModalProps> = ({
  isOpen,
  onClose,
  vehicle,
  onSaved,
}) => {
  const [checklist, setChecklist] = useState<ChecklistItem[]>([]);
  const [generalNotes, setGeneralNotes] = useState("");
  const [completedBy, setCompletedBy] = useState("");
  const [completedDate, setCompletedDate] = useState<Date | null>(new Date());
  const [saving, setSaving] = useState(false);
  const [setupRecord, setSetupRecord] = useState<VehicleSetupRecord | null>(null);
  const [loading, setLoading] = useState(false);
  const [inspectionId, setInspectionId] = useState<string>("");
  // Saved progress: an in-progress inspection is picked up where it was left, and a finished one is never marked done early.
  const [draft, setDraft] = useState<InspectionDraft | null>(null);
  const [draftLoaded, setDraftLoaded] = useState(false);
  const [photoOilDipstick, setPhotoOilDipstick] = useState("");
  const [photoOdometer, setPhotoOdometer] = useState("");
  const openingRef = useRef<string | null>(null);
  const skipFlushRef = useRef(false);
  const [vehicleTypes, setVehicleTypes] = useState<VehicleTypeRow[]>([]);

  // Same admin-configurable checklist template the driver flow reads from
  // (Settings -> Fleet Defaults -> Fleet Checks -> Vehicle Check Items) --
  // this used to be a separate hardcoded BASE_CHECKLIST here, out of sync
  // with anything an admin edited in Settings. checkItemTypeRestrictions
  // scopes items to specific vehicle types (e.g. a hoist photo only on
  // wheelchair vans); Signage/Hardware/Setup Inspection items below are a
  // separate, unrelated system driven by vehicle_setup_records.
  const [baseChecklistItems, setBaseChecklistItems] = useState<ChecklistItem[]>([]);
  const [checkItemTypeRestrictions, setCheckItemTypeRestrictions] = useState<CheckItemTypeRestrictions>(new Map());

  useEffect(() => {
    async function loadChecklistTemplate() {
      const { data, error } = await supabase
        .from("vehicle_check_items")
        .select("category, item_key, label")
        .eq("is_active", true)
        .order("sort_order", { ascending: true });
      if (error) {
        console.error("Error loading vehicle check items:", error);
        return;
      }
      setBaseChecklistItems(
        (data || []).map((item) => ({
          category: item.category,
          item_key: item.item_key,
          label: item.label,
          status: "OK" as Status,
          note: "",
        }))
      );
    }
    loadChecklistTemplate();
    loadCheckItemTypeRestrictions().then(setCheckItemTypeRestrictions);
  }, []);

  // Defaults to required -- matches the driver flow's default and the
  // "fleet_vehicle_check_require_odometer" setting in Settings -> Fleet
  // Defaults -> Fleet Checks.
  const [requireOdometerReading, setRequireOdometerReading] = useState<boolean>(true);
  const [odometerReading, setOdometerReading] = useState("");

  useEffect(() => {
    supabase
      .from("settings")
      .select("value")
      .eq("name", "fleet_vehicle_check_require_odometer")
      .maybeSingle()
      .then(({ data }) => {
        if (data?.value) setRequireOdometerReading(data.value === "true");
      });
  }, []);


  const [exteriorPhotos, setExteriorPhotos] = useState<Record<CaptureKey, string>>({
    exterior_front: "",
    exterior_rear: "",
    exterior_driver: "",
    exterior_passenger: "",
  });

  // Photo IDs for damage markers
  const [photoIds, setPhotoIds] = useState<Record<CaptureKey, string>>({
    exterior_front: "",
    exterior_rear: "",
    exterior_driver: "",
    exterior_passenger: "",
  });

  // Always the latest exterior photos, for code that runs inside long-lived callbacks.
  const exteriorPhotosRef = useRef(exteriorPhotos);
  exteriorPhotosRef.current = exteriorPhotos;
  // The marks last saved to the database for each photo, so only what changed is written.
  const syncedMarkersRef = useRef<Partial<Record<CaptureKey, FormMarker[]>>>({});

  // Damage markers per photo
  const [damageMarkers, setDamageMarkers] = useState<Record<CaptureKey, DamageMarker[]>>({
    exterior_front: [],
    exterior_rear: [],
    exterior_driver: [],
    exterior_passenger: [],
  });

  // Camera state
  const [cameraOpen, setCameraOpen] = useState(false);
  const [cameraReady, setCameraReady] = useState(false);
  const [isStreamActive, setIsStreamActive] = useState(false);
  const [cameraError, setCameraError] = useState<string>("");
  const [uploading, setUploading] = useState(false);
  const [currentCaptureKey, setCurrentCaptureKey] = useState<CaptureKey>("exterior_front");

  // Success animation state
  const [showSuccess, setShowSuccess] = useState(false);
  const [successMessage, setSuccessMessage] = useState("");

  // Video refs
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const streamRef = useRef<MediaStream | null>(null);

  // Guide controls
  const [guideVisible, setGuideVisible] = useState(true);
  const [guideOpacity, setGuideOpacity] = useState<number>(0.55);

  // Calibration
  const [calibration, setCalibration] = useState<Record<OverlayView, { x: number; y: number; scale: number }>>({
    front: { x: 0, y: 0, scale: 1 },
    rear: { x: 0, y: 0, scale: 1 },
    driver: { x: 0, y: 0, scale: 1 },
    passenger: { x: 0, y: 0, scale: 1 },
  });

  const frameSpec = useMemo(() => getFrameSpec(currentCaptureKey), [currentCaptureKey]);

  useEffect(() => {
    loadVehicleTypes().then(setVehicleTypes);
  }, []);

  // Load current user from localStorage and vehicle setup record
  useEffect(() => {
    if (isOpen) {
      // Get the logged-in user's name from localStorage
      const userName = getLoggedInUserName();
      if (userName) {
        setCompletedBy(userName);
      }
      
      // Load vehicle setup if vehicle is selected
      if (vehicle) {
        loadVehicleSetup(vehicle.vehicle_number);
        openOrResumeInspection(vehicle.vehicle_number);
        loadCalibration(vehicle.vehicle_type);
      }
    }
  }, [vehicle, isOpen]);

  // Cleanup camera on unmount or close
  useEffect(() => {
    return () => {
      if (streamRef.current) {
        streamRef.current.getTracks().forEach((t) => t.stop());
        streamRef.current = null;
      }
    };
  }, []);

  const loadCalibration = (vehicleType: string | null | undefined) => {
    if (!vehicleType) return;
    try {
      const views: OverlayView[] = ["front", "rear", "driver", "passenger"];
      const next: any = {};
      for (const v of views) {
        const key = makeCalKey(vehicleType, v);
        const raw = localStorage.getItem(key);
        if (raw) {
          const parsed = JSON.parse(raw);
          next[v] = {
            x: Number(parsed.x ?? 0),
            y: Number(parsed.y ?? 0),
            scale: Number(parsed.scale ?? 1),
          };
        } else {
          next[v] = { x: 0, y: 0, scale: 1 };
        }
      }
      setCalibration(next);
    } catch {
      // ignore
    }
  };

  const loadPhotosFor = async (id: string, legacyMarkers?: Record<string, unknown>) => {
    const { data } = await supabase
      .from("vehicle_inspection_photos")
      .select("id, photo_type, storage_path")
      .eq("inspection_id", id);
    const ext: Record<CaptureKey, string> = { exterior_front: "", exterior_rear: "", exterior_driver: "", exterior_passenger: "" };
    const ids: Record<CaptureKey, string> = { exterior_front: "", exterior_rear: "", exterior_driver: "", exterior_passenger: "" };
    let dip = "";
    let odo = "";
    for (const row of data ?? []) {
      if (row.photo_type in ext) {
        ext[row.photo_type as CaptureKey] = row.storage_path;
        ids[row.photo_type as CaptureKey] = row.id;
      } else if (row.photo_type === "oil_dipstick") dip = row.storage_path;
      else if (row.photo_type === "odometer") odo = row.storage_path;
    }
    setExteriorPhotos(ext);
    setPhotoIds(ids);
    setPhotoOilDipstick(dip);
    setPhotoOdometer(odo);

    // The marks placed so far live in the database; an older draft may still hold them in its saved answers.
    const saved = await loadMarkersForPhotos(Object.values(ids).filter(Boolean));
    const marks: Record<CaptureKey, DamageMarker[]> = { exterior_front: [], exterior_rear: [], exterior_driver: [], exterior_passenger: [] };
    const synced: Partial<Record<CaptureKey, FormMarker[]>> = {};
    for (const key of CAPTURE_KEYS) {
      const fromDb = ids[key] ? saved[ids[key]] ?? [] : [];
      if (fromDb.length) {
        marks[key] = fromDb;
        synced[key] = fromDb.map((m) => ({ ...m }));
      } else {
        const old = legacyMarkers?.[key];
        if (Array.isArray(old)) marks[key] = old as DamageMarker[];
      }
    }
    syncedMarkersRef.current = synced;
    setDamageMarkers(marks);
  };

  // Carries on an inspection that was started and not finished (within the last 30 days); otherwise starts a new one.
  // Re-using the open one also stops an empty row being left behind every time the form is opened.
  const openOrResumeInspection = async (vehicleNumber: string) => {
    if (openingRef.current === vehicleNumber) return;
    openingRef.current = vehicleNumber;
    skipFlushRef.current = false;
    setDraftLoaded(false);

    const { data: open } = await supabase
      .from("vehicle_inspections")
      .select("id, created_at, draft_state")
      .eq("vehicle_number", vehicleNumber)
      .eq("inspection_type", "admin")
      .is("completed_at", null)
      .not("progress_pct", "is", null)
      .order("created_at", { ascending: false })
      .limit(1)
      .maybeSingle();

    if (open && isDraftFresh(open.created_at)) {
      const d = (open.draft_state ?? null) as InspectionDraft | null;
      setInspectionId(open.id);
      if (d) {
        if (d.completedBy) setCompletedBy(d.completedBy);
        setOdometerReading(d.odometer ?? "");
        setGeneralNotes(d.notes ?? "");
      }
      setDraft(d);
      await loadPhotosFor(open.id, d?.damageMarkers);
      setDraftLoaded(true);
      return;
    }

    const driverName = getLoggedInUserName() || "Admin";
    const { data: created, error: createError } = await supabase
      .from("vehicle_inspections")
      .insert({
        vehicle_number: vehicleNumber,
        driver_name: driverName,
        overall_status: "OK",
        notes: "",
        inspection_type: "admin",
        progress_pct: 0,
      })
      .select("id")
      .single();

    if (!createError && created) setInspectionId(created.id);
    setDraft(null);
    setDraftLoaded(true);
  };

  const loadVehicleSetup = async (vehicleNumber: string) => {
    setLoading(true);
    try {
      const { data, error } = await supabase
        .from("vehicle_setup_records")
        .select("*")
        .eq("vehicle_number", vehicleNumber)
        .order("created_at", { ascending: false })
        .limit(1)
        .maybeSingle();

      if (error && error.code !== "PGRST116") {
        console.error("Error loading vehicle setup:", error);
      }

      setSetupRecord(data || null);
    } catch (e) {
      console.error("Error:", e);
    } finally {
      setLoading(false);
    }
  };

  // Build the full checklist including required items from setup
  const fullChecklist = useMemo(() => {
    const items: ChecklistItem[] = baseChecklistItems.filter((item) =>
      checkItemAppliesToType(item.item_key, vehicle?.vehicle_type, checkItemTypeRestrictions)
    );

    if (setupRecord) {
      // Add required signage items
      for (const item of SIGNAGE_ITEMS) {
        const value = setupRecord[item.key as keyof VehicleSetupRecord];
        if (value === "required") {
          items.push({
            category: "Signage",
            item_key: `signage_${item.key}`,
            label: item.label,
            status: "OK",
            note: "",
          });
        }
      }

      // Add required hardware items
      for (const item of HARDWARE_ITEMS) {
        const value = setupRecord[item.key as keyof VehicleSetupRecord];
        if (value === "required") {
          items.push({
            category: "Hardware",
            item_key: `hardware_${item.key}`,
            label: item.label,
            status: "OK",
            note: "",
          });
        }
      }

      // Add inspection items (always include these from setup)
      for (const item of INSPECTION_ITEMS) {
        items.push({
          category: "Setup Inspection",
          item_key: `setup_${item.key}`,
          label: item.label,
          status: "OK",
          note: "",
        });
      }
    }

    return items;
  }, [setupRecord, baseChecklistItems, checkItemTypeRestrictions, vehicle?.vehicle_type]);

  // Initialize checklist when fullChecklist changes
  useEffect(() => {
    // Every item starts unanswered; a saved draft's answers are laid back on top.
    setChecklist(applyDraftToChecklist(fullChecklist.map((item) => ({ ...item, confirmed: false })), draft));
  }, [fullChecklist, draft]);

  const checklistByCategory = useMemo(() => {
    const grouped: Record<string, ChecklistItem[]> = {};
    for (const item of checklist) {
      if (!grouped[item.category]) grouped[item.category] = [];
      grouped[item.category].push(item);
    }
    return grouped;
  }, [checklist]);

  const updateChecklistItem = (itemKey: string, field: "status" | "note", value: string) => {
    setChecklist((prev) =>
      // Choosing a status is what counts the item as done; typing a note alone doesn't.
      prev.map((item) =>
        item.item_key === itemKey ? { ...item, [field]: value, ...(field === "status" ? { confirmed: true } : {}) } : item
      )
    );
  };

  const markCategoryOk = (category: string) => {
    setChecklist((prev) =>
      prev.map((item) => (item.category === category && !item.confirmed ? { ...item, status: "OK" as Status, confirmed: true } : item))
    );
  };

  const overallStatus = useMemo(() => computeOverallStatus(checklist), [checklist]);

  const parsedOdometerReading = odometerReading.trim() ? Number(odometerReading) : null;
  const odometerReadingValid = parsedOdometerReading !== null && Number.isFinite(parsedOdometerReading) && parsedOdometerReading > 0;

  // Camera functions
  function getPublicUrl(path: string): string {
    if (!path) return "";
    const { data } = supabase.storage.from(BUCKET_VEHICLE_INSPECTIONS).getPublicUrl(path);
    return data?.publicUrl || "";
  }

  function getOverlayAssetKey(vehicleType: string | null | undefined): string | null {
    if (!vehicleType) return null;
    return vehicleTypes.find((t) => t.vehicle_type === vehicleType)?.overlay_asset_key || null;
  }

  function getOverlayUrl(view: OverlayView): string {
    const overlayAssetKey = getOverlayAssetKey(vehicle?.vehicle_type);
    if (!overlayAssetKey) return "";
    const path = getOverlayStoragePath(overlayAssetKey, view);
    return getPublicUrl(path);
  }

  async function uploadBlobToStorage(blob: Blob, photoType: string): Promise<string> {
    if (!inspectionId) throw new Error("Inspection not initialized yet.");

    const path = `inspections/${inspectionId}/${safePathPart(photoType)}_${Date.now()}.jpg`;

    const { error: uploadError } = await supabase.storage
      .from(BUCKET_VEHICLE_INSPECTIONS)
      .upload(path, blob, { upsert: true, contentType: "image/jpeg" });

    if (uploadError) throw new Error("Upload failed: " + uploadError.message);

    return path;
  }

  async function upsertPhotoRow(photoType: string, storagePath: string, category: string = "Exterior"): Promise<string> {
    const { data, error: dbError } = await supabase
      .from("vehicle_inspection_photos")
      .upsert(
        {
          inspection_id: inspectionId,
          category,
          photo_type: photoType,
          storage_path: storagePath,
        },
        { onConflict: "inspection_id,category,photo_type" }
      )
      .select("id")
      .single();

    if (dbError) throw new Error("DB insert failed: " + dbError.message);
    
    // Return the photo ID for damage markers
    return data?.id || "";
  }


  // Count captured photos
  const capturedCount = useMemo(() => {
    return Object.values(exteriorPhotos).filter(Boolean).length;
  }, [exteriorPhotos]);

  const checklistReady = !loading && baseChecklistItems.length > 0;

  const progress = useMemo(
    () =>
      computeInspectionProgress({
        exteriorPhotos: capturedCount,
        oilDipstickPhoto: !!photoOilDipstick,
        odometerRequired: requireOdometerReading,
        odometerEntered: odometerReadingValid,
        completedByEntered: !!completedBy.trim(),
        checklistTotal: checklist.length,
        checklistConfirmed: checklist.filter((i) => i.confirmed).length,
      }),
    [capturedCount, photoOilDipstick, requireOdometerReading, odometerReadingValid, completedBy, checklist]
  );

  const draftPayload = useMemo<InspectionDraft>(() => {
    // Answers saved earlier for items not in today's checklist are kept rather than dropped.
    const present = new Set(checklist.map((i) => i.item_key));
    const kept = Object.fromEntries(Object.entries(draft?.items ?? {}).filter(([k]) => !present.has(k)));
    return {
      completedBy,
      odometer: odometerReading,
      notes: generalNotes,
      items: { ...kept, ...buildDraftItems(checklist) },
    };
  }, [checklist, draft, completedBy, odometerReading, generalNotes]);

  const persistDraft = useCallback(
    async (payload: InspectionDraft, pct: number) => {
      if (!inspectionId) return;
      const { error: draftError } = await supabase
        .from("vehicle_inspections")
        .update({ progress_pct: pct, draft_state: payload })
        .eq("id", inspectionId);
      if (draftError) console.error("Could not save inspection progress:", draftError);
    },
    [inspectionId]
  );

  const sameMarkers = (a: FormMarker[], b: FormMarker[]) =>
    a.length === b.length && a.every((m) => !!m.id && b.some((x) => x.id === m.id && markerContentKey(x) === markerContentKey(m)));

  // Writes any changed damage marks to the database (the history screens and reports read them from there).
  // `strict` makes a failure throw, so finishing the inspection can't silently lose marks.
  const flushMarkers = useCallback(
    async (strict = false) => {
      if (!inspectionId) return;
      for (const key of CAPTURE_KEYS) {
        const photoId = photoIds[key];
        if (!photoId) continue;
        const current = damageMarkers[key] as FormMarker[];
        const synced = syncedMarkersRef.current[key] ?? [];
        if (sameMarkers(current, synced)) continue;
        try {
          const next = await syncMarkersForPhoto({ photoId, inspectionId, current, synced, createdBy: getLoggedInUserName() });
          syncedMarkersRef.current[key] = next;
          // Give the new marks their database ids without touching anything typed since.
          setDamageMarkers((prev) => ({
            ...prev,
            [key]: prev[key].map((m) => {
              if (m.id) return m;
              const idx = current.findIndex((c, i) => !c.id && next[i]?.id && markerContentKey(c) === markerContentKey(m));
              return idx >= 0 ? { ...m, id: next[idx].id } : m;
            }),
          }));
        } catch (e) {
          console.error("Could not save damage marks:", e);
          if (strict) throw e;
        }
      }
    },
    [inspectionId, photoIds, damageMarkers]
  );

  // A piece of damage from an earlier check the inspector says is still there: record it again on this check.
  const addKnownMarker = (view: string, marker: FormMarker) => {
    const key = view as CaptureKey;
    setDamageMarkers((prev) => ({ ...prev, [key]: [...(prev[key] ?? []), marker] }));
  };

  // Save progress a moment after the last change. Held back until the saved draft and the checklist are both
  // loaded, so a half-loaded form can never overwrite what was saved.
  useEffect(() => {
    if (!isOpen || !inspectionId || !draftLoaded || !checklistReady) return;
    const t = setTimeout(() => {
      void persistDraft(draftPayload, progress.pct);
      void flushMarkers();
    }, 800);
    return () => clearTimeout(t);
  }, [isOpen, inspectionId, draftLoaded, checklistReady, draftPayload, progress.pct, persistDraft, flushMarkers]);

  const handleEnginePhoto = async (photoType: "oil_dipstick" | "odometer", file: File | null) => {
    if (!file) return;
    setUploading(true);
    try {
      const storagePath = await uploadBlobToStorage(file, photoType);
      await upsertPhotoRow(photoType, storagePath, "Engine");
      if (photoType === "oil_dipstick") setPhotoOilDipstick(storagePath);
      else setPhotoOdometer(storagePath);
    } catch (e: any) {
      toast({ title: "Photo not saved", description: e?.message ?? "Upload failed.", variant: "destructive" });
    } finally {
      setUploading(false);
    }
  };

  // Open camera modal
  const openCameraModal = useCallback(() => {
    setCameraError("");
    setCameraReady(false);
    setIsStreamActive(false);
    setCurrentCaptureKey("exterior_front");
    setCameraOpen(true);
    setShowSuccess(false);

    if (isIOSDevice()) {
      setCameraError(
        isStandalonePWA()
          ? "iPhone tip: Live preview can be black in PWA. If that happens, use 'Phone Camera'."
          : "iPhone tip: If the live preview is black, use 'Phone Camera'."
      );
    }
  }, []);

  const stopCamera = useCallback(() => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((t) => t.stop());
      streamRef.current = null;
    }
    if (videoRef.current) {
      // @ts-ignore
      videoRef.current.srcObject = null;
    }
    setCameraReady(false);
    setIsStreamActive(false);
  }, []);

  const closeCameraModal = useCallback(() => {
    stopCamera();
    setCameraOpen(false);
    setShowSuccess(false);
  }, [stopCamera]);

  async function waitForVideoDimensions(video: HTMLVideoElement, timeoutMs = 2500) {
    const start = performance.now();
    return await new Promise<boolean>((resolve) => {
      const tick = () => {
        const w = video.videoWidth || 0;
        const h = video.videoHeight || 0;
        if (w > 0 && h > 0) return resolve(true);
        if (performance.now() - start > timeoutMs) return resolve(false);
        requestAnimationFrame(tick);
      };
      tick();
    });
  }

  const enableCamera = useCallback(async () => {
    setCameraError("");
    setCameraReady(false);
    setIsStreamActive(false);

    try {
      if (!navigator.mediaDevices?.getUserMedia) {
        throw new Error("Camera is not supported in this browser.");
      }

      stopCamera();

      // Request rear camera (environment-facing) for vehicle photos
      // Try with exact constraint first, fall back to ideal
      let stream: MediaStream;
      try {
        stream = await navigator.mediaDevices.getUserMedia({
          video: { 
            facingMode: "environment"  // Use rear camera - simpler constraint
          },
          audio: false,
        });
      } catch (e) {
        // If exact environment fails, try with ideal
        stream = await navigator.mediaDevices.getUserMedia({
          video: { 
            facingMode: { ideal: "environment" }
          },
          audio: false,
        });
      }

      streamRef.current = stream;

      const video = videoRef.current;
      if (!video) throw new Error("Video element not available (ref not mounted).");

      video.setAttribute("playsinline", "true");
      video.muted = true;
      video.autoplay = true;

      // @ts-ignore
      video.srcObject = stream;

      try {
        await video.play();
      } catch {
        // If autoplay is blocked, it may still show after user interaction
      }

      const ok = await waitForVideoDimensions(video, 3000);

      if (!ok) {
        setCameraError("Live camera preview is not available on this device/browser. Use Phone Camera instead.");
        setCameraReady(false);
        setIsStreamActive(false);
        stopCamera();
        return;
      }

      setIsStreamActive(true);
      setCameraReady(true);
    } catch (e: any) {
      setCameraError(e?.message || "Camera permission was denied or not available.");
      setCameraReady(false);
      setIsStreamActive(false);
      stopCamera();
    }
  }, [stopCamera]);

  const noteExteriorPhoto = useCallback((key: CaptureKey, id: string) => {
    setPhotoIds((prev) => ({ ...prev, [key]: id }));
    // A retaken photo no longer matches the marks placed on the old one.
    if (exteriorPhotosRef.current[key]) setDamageMarkers((prev) => ({ ...prev, [key]: [] }));
  }, []);

  // Function to handle successful capture and auto-advance
  const handleCaptureSuccess = useCallback((capturedKey: CaptureKey, storagePath: string) => {
    // Update the photo state
    setExteriorPhotos((prev) => {
      const updated = { ...prev, [capturedKey]: storagePath };
      
      // Calculate new captured count
      const newCapturedCount = Object.values(updated).filter(Boolean).length;

      // Find next uncaptured photo
      const currentIndex = CAPTURE_KEYS.indexOf(capturedKey);
      let nextKey: CaptureKey | null = null;

      // First try to find next uncaptured photo after current
      for (let i = currentIndex + 1; i < CAPTURE_KEYS.length; i++) {
        const key = CAPTURE_KEYS[i];
        if (!updated[key]) {
          nextKey = key;
          break;
        }
      }

      // If not found, try from beginning
      if (!nextKey) {
        for (let i = 0; i < currentIndex; i++) {
          const key = CAPTURE_KEYS[i];
          if (!updated[key]) {
            nextKey = key;
            break;
          }
        }
      }

      // Show success animation
      setShowSuccess(true);
      if (newCapturedCount === 4) {
        setSuccessMessage("All exterior photos captured!");
      } else {
        setSuccessMessage(`${VIEW_LABELS[capturedKey].split(" ")[0]} captured!`);
      }

      // After brief delay, either advance or close
      setTimeout(() => {
        setShowSuccess(false);
        if (newCapturedCount === 4) {
          // All photos captured - close modal
          closeCameraModal();
        } else if (nextKey) {
          // Advance to next uncaptured photo
          setCurrentCaptureKey(nextKey);
        }
      }, 1200);

      return updated;
    });
  }, [closeCameraModal]);

  const captureAndUploadFromVideo = useCallback(async () => {
    if (!videoRef.current) return;
    setUploading(true);

    try {
      const video = videoRef.current;
      const w = video.videoWidth || 0;
      const h = video.videoHeight || 0;

      if (w === 0 || h === 0) {
        throw new Error("Live camera preview failed. Please use Phone Camera instead.");
      }

      const canvas = document.createElement("canvas");
      canvas.width = w;
      canvas.height = h;

      const ctx = canvas.getContext("2d");
      if (!ctx) throw new Error("Could not access canvas.");

      ctx.drawImage(video, 0, 0, w, h);

      const blob: Blob = await new Promise((resolve, reject) => {
        canvas.toBlob(
          (b) => (b ? resolve(b) : reject(new Error("Could not create image blob."))),
          "image/jpeg",
          0.9
        );
      });

      const storagePath = await uploadBlobToStorage(blob, currentCaptureKey);
      noteExteriorPhoto(currentCaptureKey, await upsertPhotoRow(currentCaptureKey, storagePath));

      handleCaptureSuccess(currentCaptureKey, storagePath);
    } catch (e: any) {
      setCameraError(e?.message || "Capture/upload failed.");
    } finally {
      setUploading(false);
    }
  }, [currentCaptureKey, inspectionId, handleCaptureSuccess]);

  const handleNativeCapture = useCallback(async (file: File | null) => {
    if (!file) return;

    setUploading(true);

    try {
      const storagePath = await uploadBlobToStorage(file, currentCaptureKey);
      noteExteriorPhoto(currentCaptureKey, await upsertPhotoRow(currentCaptureKey, storagePath));

      handleCaptureSuccess(currentCaptureKey, storagePath);
    } catch (e: any) {
      setCameraError(e?.message || "Upload failed.");
    } finally {
      setUploading(false);
    }
  }, [currentCaptureKey, inspectionId, handleCaptureSuccess]);

  // Navigation between views
  const goToPreviousView = useCallback(() => {
    const currentIndex = CAPTURE_KEYS.indexOf(currentCaptureKey);
    if (currentIndex > 0) {
      setCurrentCaptureKey(CAPTURE_KEYS[currentIndex - 1]);
    }
  }, [currentCaptureKey]);

  const goToNextView = useCallback(() => {
    const currentIndex = CAPTURE_KEYS.indexOf(currentCaptureKey);
    if (currentIndex < CAPTURE_KEYS.length - 1) {
      setCurrentCaptureKey(CAPTURE_KEYS[currentIndex + 1]);
    }
  }, [currentCaptureKey]);

  const overlayForCurrentCapture = useMemo(() => {
    if (!vehicle?.vehicle_type) return "";
    const view = captureKeyToView(currentCaptureKey);
    return getOverlayUrl(view);
  }, [vehicle?.vehicle_type, currentCaptureKey, vehicleTypes]);

  const currentView = captureKeyToView(currentCaptureKey);
  const currentCal = calibration[currentView];
  const currentIndex = CAPTURE_KEYS.indexOf(currentCaptureKey);

  const saveCalibrationForCurrent = useCallback(() => {
    if (!vehicle?.vehicle_type) return;
    const vt = vehicle.vehicle_type;
    const key = makeCalKey(vt, currentView);
    localStorage.setItem(key, JSON.stringify(calibration[currentView]));
    toast({
      title: "Calibration saved",
      description: `Saved calibration for ${currentView} view.`,
    });
  }, [vehicle?.vehicle_type, currentView, calibration]);

  const resetCalibrationForCurrent = useCallback(() => {
    setCalibration((prev) => ({
      ...prev,
      [currentView]: { x: 0, y: 0, scale: 1 },
    }));
  }, [currentView]);

  // "Complete" is the only thing that marks an inspection done, and only once everything required is done.
  const handleSave = async () => {
    if (!vehicle) return;

    if (!progress.complete) {
      toast({
        title: "Not finished yet",
        description: "Still to do: " + progress.missing.join(", ") + ".",
        variant: "destructive",
      });
      return;
    }

    setSaving(true);

    try {
      // Every damage mark must be saved before the inspection counts as finished.
      await flushMarkers(true);

      // Update the existing inspection record
      const { error: updateError } = await supabase
        .from("vehicle_inspections")
        .update({
          driver_name: completedBy,
          overall_status: overallStatus,
          notes: generalNotes,
          odometer_km: parsedOdometerReading,
          // See vehicle_inspections.completed_at -- marks this record as
          // actually finished, not just started.
          completed_at: new Date().toISOString(),
          progress_pct: 100,
          draft_state: null,
        })
        .eq("id", inspectionId);

      if (updateError) throw updateError;

      // Insert checklist items
      const checklistRows = checklist.map((item) => ({
        inspection_id: inspectionId,
        category: item.category,
        item_key: item.item_key,
        label: item.label,
        status: item.status,
        note: item.note,
      }));

      const { error: itemsError } = await supabase
        .from("vehicle_inspection_items")
        .insert(checklistRows);

      if (itemsError) throw itemsError;

      // Wire the reading into the vehicle's "last KM" (only if higher than
      // what's on file -- see recordInspectionOdometer) so it feeds
      // RUC/service/tyre-rotation tracking in FleetManagement.
      if (parsedOdometerReading) {
        await recordInspectionOdometer(vehicle.vehicle_number, parsedOdometerReading, completedBy);
      }

      toast({
        title: "Inspection completed",
        description: "Admin inspection for " + vehicle.vehicle_number + " is complete.",
      });

      skipFlushRef.current = true;
      await handleClose();
    } catch (e: any) {
      toast({
        title: "Error saving inspection",
        description: e?.message ?? "Unknown error",
        variant: "destructive",
      });
    } finally {
      setSaving(false);
    }
  };

  // Closing never throws progress away: it is saved first, and the form picks up from there next time.
  const handleClose = async () => {
    if (!skipFlushRef.current && inspectionId && draftLoaded && checklistReady) {
      await persistDraft(draftPayload, progress.pct);
      await flushMarkers();
    }
    stopCamera();
    setChecklist([]);
    setGeneralNotes("");
    setCompletedBy("");
    setCompletedDate(new Date());
    setSetupRecord(null);
    setInspectionId("");
    setOdometerReading("");
    setPhotoOilDipstick("");
    setPhotoOdometer("");
    setDraft(null);
    setDraftLoaded(false);
    openingRef.current = null;
    setExteriorPhotos({
      exterior_front: "",
      exterior_rear: "",
      exterior_driver: "",
      exterior_passenger: "",
    });
    setDamageMarkers({ exterior_front: [], exterior_rear: [], exterior_driver: [], exterior_passenger: [] });
    setPhotoIds({ exterior_front: "", exterior_rear: "", exterior_driver: "", exterior_passenger: "" });
    syncedMarkersRef.current = {};
    setCameraOpen(false);
    setShowSuccess(false);
    onSaved();
    onClose();
  };

  if (!vehicle) return null;

  // Category order for display
  const categoryOrder = [
    "Fluids",
    "Oil",
    "Brakes",
    "Camera",
    "Lights",
    "Tyres",
    "Interior",
    "Exterior",
    "Safety",
    "Signage",
    "Hardware",
    "Setup Inspection",
  ];

  const sortedCategories = Object.keys(checklistByCategory).sort((a, b) => {
    const aIdx = categoryOrder.indexOf(a);
    const bIdx = categoryOrder.indexOf(b);
    if (aIdx === -1 && bIdx === -1) return a.localeCompare(b);
    if (aIdx === -1) return 1;
    if (bIdx === -1) return -1;
    return aIdx - bIdx;
  });

  return (
    <>
      <Dialog open={isOpen && !cameraOpen} onOpenChange={(open) => !open && !cameraOpen && handleClose()}>
        <DialogContent className="max-w-5xl max-h-[90vh] overflow-y-auto bg-gray-900 border-gray-700">

          <DialogHeader className="flex flex-col gap-3 sticky top-0 bg-gray-900 z-10 pb-4 border-b border-gray-700">
            <div className="flex flex-row items-center justify-between gap-2">
              <DialogTitle className="text-lg font-semibold text-amber-500 flex items-center gap-2">
                <ClipboardCheck className="w-5 h-5" />
                Admin Inspection: {vehicle.vehicle_number} - {vehicle.make} {vehicle.model}
              </DialogTitle>
              <div className="flex gap-2">
                <Button
                  onClick={handleSave}
                  disabled={saving || !progress.complete}
                  title={progress.complete ? "Mark this inspection as completed" : "Finish every item first"}
                  className="bg-green-600 hover:bg-green-700"
                >
                  <Save className="w-4 h-4 mr-2" />
                  {saving ? "Completing..." : "Complete inspection"}
                </Button>
                <Button variant="outline" onClick={() => void handleClose()} title="Your progress is saved so you can carry on later">
                  <X className="w-4 h-4 mr-2" />
                  Save &amp; close
                </Button>
              </div>
            </div>
            <div>
              <div className="flex items-center justify-between gap-3 text-sm">
                <span className={progress.complete ? "font-semibold text-green-400" : "font-semibold text-gray-200"}>
                  {progress.complete ? "\u2713 100% - everything is done, ready to complete" : progress.pct + "% complete"}
                </span>
                {!progress.complete && (
                  <span className="text-xs text-gray-400 text-right">Still to do: {progress.missing.join(", ")}</span>
                )}
              </div>
              <div className="h-2 rounded bg-gray-700 mt-1.5 overflow-hidden">
                <div
                  className={progress.complete ? "h-full bg-green-500 transition-all" : "h-full bg-amber-500 transition-all"}
                  style={{ width: progress.pct + "%" }}
                />
              </div>
            </div>
          </DialogHeader>

          {loading ? (
            <div className="py-8 text-center text-muted-foreground">Loading vehicle setup...</div>
          ) : (
            <div className="space-y-4 pt-4">
              {/* Completed By and Date */}
              <Section title="Inspection Details" subtitle="Who completed this inspection">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Completed By *</Label>
                    <Input
                      value={completedBy}
                      onChange={(e) => setCompletedBy(e.target.value)}
                      placeholder="Enter name..."
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Date</Label>
                    <Popover>
                      <PopoverTrigger asChild>
                        <Button
                          variant="outline"
                          className={cn(
                            "w-full justify-start text-left font-normal",
                            !completedDate && "text-muted-foreground"
                          )}
                        >
                          <CalendarIcon className="mr-2 h-4 w-4 text-primary" />
                          {completedDate ? formatDate(completedDate) : "Select date..."}
                        </Button>
                      </PopoverTrigger>
                      <PopoverContent className="w-auto p-0" align="start">
                        <Calendar
                          mode="single"
                          selected={completedDate || undefined}
                          onSelect={(d) => setCompletedDate(d || null)}
                          initialFocus
                        />
                      </PopoverContent>
                    </Popover>
                  </div>
                </div>
              </Section>

              {/* Odometer Reading */}
              <Section title="Odometer" subtitle="Current reading from the vehicle's dashboard">
                <div className="max-w-xs space-y-2">
                  <Label>
                    Odometer Reading (km) {requireOdometerReading && <span className="text-red-500">*</span>}
                  </Label>
                  <Input
                    type="number"
                    inputMode="numeric"
                    min={1}
                    value={odometerReading}
                    onChange={(e) => setOdometerReading(e.target.value)}
                    placeholder="e.g. 128450"
                  />
                </div>
              </Section>

              {/* Overall Status Preview */}
              <div
                className="p-4 rounded-lg"
                style={{
                  backgroundColor: `${STATUS_COLOR[overallStatus]}15`,
                  borderLeft: `4px solid ${STATUS_COLOR[overallStatus]}`,
                }}
              >
                <p className="text-sm text-gray-400">Overall Status</p>
                <p className="text-xl font-bold" style={{ color: STATUS_COLOR[overallStatus] }}>
                  {overallStatus}
                </p>
              </div>

              {/* Exterior Photos Section */}
              <Section title="Exterior Photos" subtitle="Take photos of all four sides of the vehicle">
                <div className="space-y-4">
                  {/* Photo thumbnails grid */}
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                    {CAPTURE_KEYS.map((key) => {
                      const photo = exteriorPhotos[key];
                      const label = VIEW_LABELS[key].split(" ")[0]; // Just "FRONT", "REAR", etc.
                      return (
                        <div key={key} className="border border-gray-600 rounded-lg p-2 bg-gray-800/50">
                          <div className="text-center mb-2">
                            <h3 className="font-medium text-gray-200 text-sm">{label}</h3>
                          </div>
                          {photo ? (
                            <div className="relative">
                              <img
                                src={getPublicUrl(photo)}
                                alt={label}
                                className="w-full h-16 object-cover rounded-lg"
                              />
                              <div className="absolute top-1 right-1 bg-green-500 rounded-full p-0.5">
                                <svg className="w-3 h-3 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                                </svg>
                              </div>
                            </div>
                          ) : (
                            <div className="w-full h-16 bg-gray-700/50 rounded-lg flex items-center justify-center">
                              <Camera className="w-5 h-5 text-gray-500" />
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>

                  {/* Open Camera Button */}
                  <button
                    onClick={openCameraModal}
                    className="w-full bg-blue-600 hover:bg-blue-700 text-white font-medium py-3 rounded-lg transition-colors flex items-center justify-center gap-2"
                  >
                    <Camera className="w-5 h-5" />
                    {capturedCount === 0 
                      ? "Take Exterior Photos" 
                      : capturedCount === 4 
                        ? "Retake Exterior Photos" 
                        : `Continue Taking Photos (${capturedCount}/4)`}
                  </button>
                </div>
              </Section>

              {/* Engine bay: the same oil dipstick photo (required) and optional odometer photo the driver check takes */}
              <Section title="Engine Bay" subtitle="Oil dipstick photo is required; the odometer photo is optional">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  {([
                    { type: "oil_dipstick" as const, label: "Oil Dipstick", hint: "Required", path: photoOilDipstick },
                    { type: "odometer" as const, label: "Odometer", hint: "Photo optional", path: photoOdometer },
                  ]).map((slot) => (
                    <div key={slot.type} className="border border-gray-600 rounded-lg p-3 bg-gray-800/50">
                      <div className="flex items-center justify-between mb-2">
                        <h3 className="font-medium text-gray-200 text-sm">{slot.label}</h3>
                        <span className="text-xs text-gray-400">{slot.hint}</span>
                      </div>
                      {slot.path ? (
                        <div className="relative">
                          <img src={getPublicUrl(slot.path)} alt={slot.label} className="w-full h-28 object-cover rounded-lg" />
                          <div className="absolute top-1 right-1 bg-green-500 rounded-full p-0.5">
                            <svg className="w-3 h-3 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                            </svg>
                          </div>
                        </div>
                      ) : (
                        <div className="w-full h-28 bg-gray-700/50 rounded-lg flex items-center justify-center">
                          <Camera className="w-6 h-6 text-gray-500" />
                        </div>
                      )}
                      <label className="mt-2 flex w-full cursor-pointer items-center justify-center gap-2 rounded-lg bg-blue-600 py-2 text-sm font-medium text-white hover:bg-blue-700">
                        <Camera className="w-4 h-4" />
                        {uploading ? "Uploading..." : slot.path ? "Retake photo" : "Take photo"}
                        <input
                          type="file"
                          accept="image/*"
                          capture="environment"
                          className="hidden"
                          disabled={uploading || !inspectionId}
                          onChange={(e) => {
                            void handleEnginePhoto(slot.type, e.target.files?.[0] ?? null);
                            e.target.value = "";
                          }}
                        />
                      </label>
                    </div>
                  ))}
                </div>
              </Section>

              {/* Damage found on earlier checks: still there, or repaired? */}
              {inspectionId && (
                <KnownDamagePanel
                  vehicleNumber={vehicle.vehicle_number}
                  inspectionId={inspectionId}
                  photoIds={photoIds}
                  markers={damageMarkers}
                  onStillThere={addKnownMarker}
                />
              )}

              {/* Damage Markers Section - Only show if photos are captured */}
              {capturedCount > 0 && (
                <Section title="Mark Damage Areas" subtitle="Click on photos to mark damage locations with notes">
                  <div className="space-y-6">
                    {CAPTURE_KEYS.map((key) => {
                      const photo = exteriorPhotos[key];
                      if (!photo) return null;
                      return (
                        <DamageMarkerOverlay
                          key={key}
                          imageUrl={getPublicUrl(photo)}
                          photoType={key}
                          markers={damageMarkers[key]}
                          onMarkersChange={(newMarkers) => {
                            setDamageMarkers((prev) => ({
                              ...prev,
                              [key]: newMarkers,
                            }));
                          }}
                          readOnly={false}
                        />
                      );
                    })}
                  </div>
                </Section>
              )}

              {/* Checklist by Category */}

              <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                {sortedCategories.map((category) => (
                  <Section key={category} title={category}>
                    <div className="space-y-2">
                      <div className="flex items-center justify-between text-xs text-gray-400">
                        <span>
                          {checklistByCategory[category].filter((i) => i.confirmed).length} of {checklistByCategory[category].length} done
                        </span>
                        {checklistByCategory[category].some((i) => !i.confirmed) && (
                          <button
                            type="button"
                            onClick={() => markCategoryOk(category)}
                            className="rounded border border-green-700 px-2 py-0.5 text-green-400 hover:bg-green-900/30"
                          >
                            Mark all OK
                          </button>
                        )}
                      </div>
                      {checklistByCategory[category].map((item) => (
                        <div key={item.item_key} className="bg-muted/20 rounded-lg p-3">
                          <div className="flex flex-col sm:flex-row sm:items-center gap-2">
                            <span className="flex-1 text-sm text-gray-200">{item.label}</span>
                            <Select
                              value={item.confirmed ? item.status : ""}
                              onValueChange={(v) => updateChecklistItem(item.item_key, "status", v)}
                            >
                              <SelectTrigger
                                className="w-[160px]"
                                style={
                                  item.confirmed
                                    ? {
                                        borderColor: STATUS_COLOR[item.status],
                                        backgroundColor: `${STATUS_COLOR[item.status]}15`,
                                      }
                                    : undefined
                                }
                              >
                                <SelectValue placeholder="Tap to confirm" />
                              </SelectTrigger>
                              <SelectContent>
                                {STATUS_OPTIONS.map((opt) => (
                                  <SelectItem key={opt.value} value={opt.value}>
                                    {opt.label}
                                  </SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          </div>

                          {item.status !== "OK" && (
                            <Textarea
                              value={item.note}
                              onChange={(e) => updateChecklistItem(item.item_key, "note", e.target.value)}
                              placeholder="Add notes about the issue..."
                              className="mt-2 text-sm"
                              rows={2}
                            />
                          )}
                        </div>
                      ))}
                    </div>
                  </Section>
                ))}
              </div>

              {/* General Notes */}
              <Section title="General Notes" subtitle="Any additional observations">
                <Textarea
                  value={generalNotes}
                  onChange={(e) => setGeneralNotes(e.target.value)}
                  placeholder="Add any general notes about the inspection..."
                  rows={3}
                />
              </Section>
            </div>
          )}
        </DialogContent>
      </Dialog>



      {/* Camera Modal - rendered via portal to escape Dialog's stacking context */}
      {cameraOpen && createPortal(
        <div 
          className="fixed inset-0 bg-black/60"
          style={{ 
            zIndex: 999999,
            touchAction: 'none',
            overscrollBehavior: 'contain',
            pointerEvents: 'auto'
          }}
        >
          <div 
            className="absolute inset-0 flex items-start justify-center overflow-y-auto py-4 px-3"
            style={{ 
              touchAction: 'pan-y',
              WebkitOverflowScrolling: 'touch',
              overscrollBehavior: 'contain',
              pointerEvents: 'auto'
            }}
            onClick={(e) => {
              if (e.target === e.currentTarget) {
                closeCameraModal();
              }
            }}
          >
            <div 
              className="w-full max-w-md bg-white rounded-2xl shadow-xl my-auto"
              style={{ pointerEvents: 'auto' }}
              onClick={(e) => e.stopPropagation()}
            >
              {/* Header */}
              <div className="px-4 py-3 border-b flex items-center justify-between sticky top-0 bg-white rounded-t-2xl z-10">
                <div>
                  <div className="font-semibold text-gray-900">{VIEW_LABELS[currentCaptureKey]}</div>
                  <div className="text-xs text-gray-500">
                    {currentIndex + 1} of {CAPTURE_KEYS.length} • {frameSpec.label}
                  </div>
                </div>
                <button 
                  type="button"
                  onClick={closeCameraModal}
                  className="text-gray-500 hover:text-gray-700 text-2xl leading-none p-2 -mr-2"
                >
                  ×
                </button>
              </div>

              {/* Photo status indicators */}
              <div className="bg-gray-50 px-4 py-2 flex justify-center gap-2 border-b">
                {CAPTURE_KEYS.map((key) => (
                  <button
                    key={key}
                    type="button"
                    onClick={() => setCurrentCaptureKey(key)}
                    className={cn(
                      "px-3 py-1 rounded-full text-xs font-medium transition-colors",
                      key === currentCaptureKey
                        ? "bg-amber-500 text-white"
                        : exteriorPhotos[key]
                          ? "bg-green-500 text-white"
                          : "bg-gray-200 text-gray-600 hover:bg-gray-300"
                    )}
                  >
                    {VIEW_LABELS[key].split(" ")[0]}
                  </button>
                ))}
              </div>

              {/* Content area */}
              <div className="p-4 space-y-4">
                {/* Video preview */}
                <div className="relative w-full overflow-hidden rounded-2xl bg-black border border-gray-300" style={{ maxHeight: '40vh' }}>
                  <div style={{ paddingTop: aspectToPaddingTop(frameSpec.aspect), maxHeight: '40vh' }} />
                  <div className="absolute inset-0">
                    <video
                      ref={videoRef}
                      playsInline
                      muted
                      autoPlay
                      className="absolute inset-0 w-full h-full"
                      style={{
                        objectFit: "cover",
                        opacity: isStreamActive ? 1 : 0,
                        transform: "translateZ(0)",
                        WebkitTransform: "translateZ(0)",
                      }}
                    />

                    {/* Overlay */}
                    {guideVisible && overlayForCurrentCapture && (
                      <img
                        src={overlayForCurrentCapture}
                        alt="overlay"
                        className="absolute inset-0 w-full h-full pointer-events-none"
                        style={{
                          objectFit: "cover",
                          opacity: guideOpacity,
                          transform: `translate(${currentCal.x}px, ${currentCal.y}px) scale(${currentCal.scale})`,
                          transformOrigin: "center center",
                          zIndex: 10,
                        }}
                      />
                    )}

                    {/* Placeholder when no stream */}
                    {!isStreamActive && !showSuccess && (
                      <div className="absolute inset-0 flex items-center justify-center text-white/70 text-sm z-[5]">
                        Camera preview will appear here
                      </div>
                    )}

                    {/* Success animation overlay */}
                    {showSuccess && (
                      <div className="absolute inset-0 flex flex-col items-center justify-center bg-green-500/90 z-30 animate-pulse">
                        <div className="w-20 h-20 bg-white rounded-full flex items-center justify-center mb-4">
                          <svg className="w-12 h-12 text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                          </svg>
                        </div>
                        <div className="text-white text-xl font-bold">{successMessage}</div>
                        {capturedCount < 4 && (
                          <div className="text-white/80 text-sm mt-2">Advancing to next photo...</div>
                        )}
                      </div>
                    )}

                    {/* Current photo indicator */}
                    {exteriorPhotos[currentCaptureKey] && !showSuccess && (
                      <div className="absolute top-2 left-2 bg-green-500 text-white text-xs px-2 py-1 rounded-full flex items-center gap-1 z-20">
                        <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                        </svg>
                        Photo captured
                      </div>
                    )}
                  </div>
                </div>

                {/* Navigation buttons */}
                <div className="flex gap-2">
                  <button
                    type="button"
                    onClick={goToPreviousView}
                    disabled={currentIndex === 0}
                    className={cn(
                      "flex-1 py-2 rounded-lg font-medium text-sm transition-colors",
                      currentIndex === 0
                        ? "bg-gray-100 text-gray-400 cursor-not-allowed"
                        : "bg-gray-200 text-gray-700 hover:bg-gray-300 active:bg-gray-400"
                    )}
                  >
                    ← Previous
                  </button>
                  <button
                    type="button"
                    onClick={goToNextView}
                    disabled={currentIndex === CAPTURE_KEYS.length - 1}
                    className={cn(
                      "flex-1 py-2 rounded-lg font-medium text-sm transition-colors",
                      currentIndex === CAPTURE_KEYS.length - 1
                        ? "bg-gray-100 text-gray-400 cursor-not-allowed"
                        : "bg-gray-200 text-gray-700 hover:bg-gray-300 active:bg-gray-400"
                    )}
                  >
                    Next →
                  </button>
                </div>

                {/* Action buttons */}
                {!cameraReady ? (
                  <>
                    <button
                      type="button"
                      onClick={enableCamera}
                      className="w-full bg-blue-600 hover:bg-blue-700 active:bg-blue-800 text-white font-semibold py-3 rounded-xl"
                      disabled={showSuccess}
                    >
                      Enable Camera
                    </button>

                    <label className="block">
                      <div className="w-full text-center border border-gray-300 rounded-xl py-3 font-semibold text-gray-800 bg-white hover:bg-gray-50 active:bg-gray-100 cursor-pointer">
                        Use Phone Camera (Recommended on iPhone/PWA)
                      </div>
                      <input
                        type="file"
                        accept="image/*"
                        capture="environment"
                        className="hidden"
                        onChange={(e) => handleNativeCapture(e.target.files?.[0] || null)}
                        disabled={uploading || showSuccess}
                      />
                    </label>
                  </>
                ) : (
                  <>
                    <div className="flex gap-2">
                      <button
                        type="button"
                        onClick={captureAndUploadFromVideo}
                        className="flex-1 bg-green-600 hover:bg-green-700 active:bg-green-800 text-white font-semibold py-3 rounded-xl"
                        disabled={uploading || showSuccess}
                      >
                        {uploading ? "Uploading..." : exteriorPhotos[currentCaptureKey] ? "Retake Photo" : "Capture Photo"}
                      </button>
                      <button
                        type="button"
                        onClick={() => {
                          stopCamera();
                          setCameraReady(false);
                        }}
                        className="px-4 py-3 rounded-xl border border-gray-300 text-gray-700 hover:bg-gray-50 active:bg-gray-100"
                        disabled={uploading || showSuccess}
                      >
                        Stop
                      </button>
                    </div>

                    <label className="block">
                      <div className="w-full text-center border border-gray-300 rounded-xl py-3 font-semibold text-gray-800 bg-white hover:bg-gray-50 active:bg-gray-100 cursor-pointer">
                        Use Phone Camera Instead
                      </div>
                      <input
                        type="file"
                        accept="image/*"
                        capture="environment"
                        className="hidden"
                        onChange={(e) => handleNativeCapture(e.target.files?.[0] || null)}
                        disabled={uploading || showSuccess}
                      />
                    </label>
                  </>
                )}

                {/* Guide controls */}
                <div className="bg-gray-50 border border-gray-200 rounded-xl p-3 space-y-3">
                  <div className="flex items-center justify-between">
                    <div className="text-sm font-semibold text-gray-800">Guide Overlay</div>
                    <button
                      type="button"
                      onClick={() => setGuideVisible((v) => !v)}
                      className="text-sm font-semibold text-blue-700 hover:text-blue-900"
                    >
                      {guideVisible ? "Hide" : "Show"}
                    </button>
                  </div>

                  <div>
                    <div className="flex items-center justify-between text-xs text-gray-600 mb-1">
                      <span>Opacity</span>
                      <span>{Math.round(guideOpacity * 100)}%</span>
                    </div>
                    <input
                      type="range"
                      min={0}
                      max={1}
                      step={0.05}
                      value={guideOpacity}
                      onChange={(e) => setGuideOpacity(parseFloat(e.target.value))}
                      className="w-full"
                    />
                  </div>

                  {/* Calibration controls */}
                  <div className="pt-2 border-t border-gray-200 space-y-3">
                    <div className="text-sm font-semibold text-gray-800">Calibration</div>

                    <div>
                      <div className="flex items-center justify-between text-xs text-gray-600 mb-1">
                        <span>X offset</span>
                        <span>{calibration[currentView].x}px</span>
                      </div>
                      <input
                        type="range"
                        min={-200}
                        max={200}
                        step={1}
                        value={calibration[currentView].x}
                        onChange={(e) =>
                          setCalibration((prev) => ({
                            ...prev,
                            [currentView]: { ...prev[currentView], x: parseInt(e.target.value, 10) },
                          }))
                        }
                        className="w-full"
                      />
                    </div>

                    <div>
                      <div className="flex items-center justify-between text-xs text-gray-600 mb-1">
                        <span>Y offset</span>
                        <span>{calibration[currentView].y}px</span>
                      </div>
                      <input
                        type="range"
                        min={-200}
                        max={200}
                        step={1}
                        value={calibration[currentView].y}
                        onChange={(e) =>
                          setCalibration((prev) => ({
                            ...prev,
                            [currentView]: { ...prev[currentView], y: parseInt(e.target.value, 10) },
                          }))
                        }
                        className="w-full"
                      />
                    </div>

                    <div>
                      <div className="flex items-center justify-between text-xs text-gray-600 mb-1">
                        <span>Scale</span>
                        <span>{calibration[currentView].scale.toFixed(2)}x</span>
                      </div>
                      <input
                        type="range"
                        min={0.5}
                        max={2.0}
                        step={0.01}
                        value={calibration[currentView].scale}
                        onChange={(e) =>
                          setCalibration((prev) => ({
                            ...prev,
                            [currentView]: { ...prev[currentView], scale: parseFloat(e.target.value) },
                          }))
                        }
                        className="w-full"
                      />
                    </div>

                    <div className="flex gap-2">
                      <button
                        type="button"
                        onClick={saveCalibrationForCurrent}
                        className="flex-1 bg-blue-600 hover:bg-blue-700 active:bg-blue-800 text-white font-semibold py-2 rounded-lg"
                      >
                        Save
                      </button>
                      <button
                        type="button"
                        onClick={resetCalibrationForCurrent}
                        className="px-4 py-2 rounded-lg border border-gray-300 text-gray-700 hover:bg-gray-50 active:bg-gray-100"
                      >
                        Reset
                      </button>
                    </div>

                    <div className="text-xs text-gray-500">
                      Calibration saves per vehicle type + view on this device.
                    </div>
                  </div>
                </div>

                {uploading && <div className="text-center text-blue-600 animate-pulse">Uploading...</div>}

                {cameraError && (
                  <div className="text-sm text-amber-800 bg-amber-50 border border-amber-200 p-3 rounded-lg">
                    {cameraError}
                  </div>
                )}

                <div className="text-xs text-gray-500 text-center">
                  If live preview shows black on iPhone Safari/Chrome/PWA, use "Phone Camera".
                </div>

                {/* Done button */}
                <button
                  type="button"
                  onClick={closeCameraModal}
                  className="w-full bg-gray-700 hover:bg-gray-600 active:bg-gray-500 text-white font-semibold py-3 rounded-xl"
                  disabled={showSuccess}
                >
                  Done ({capturedCount}/4 photos)
                </button>
              </div>
            </div>
          </div>
        </div>,
        document.body
      )}
    </>
  );
};

export default AdminVehicleInspectionModal;
